import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import supportTicketService from '../../services/supportTicketService';
import PerformanceSummaryCard from './components/PerformanceSummaryCard';
import AgentPerformanceTable from './components/AgentPerformanceTable';
import IssueCategoryChart from './components/IssueCategoryChart';
import TicketTrendChart from './components/TicketTrendChart';
import SatisfactionAnalysisPanel from './components/SatisfactionAnalysisPanel';
import SLACompliancePanel from './components/SLACompliancePanel';

const SupportAnalyticsPerformanceDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [ticketTrends, setTicketTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [analyticsData, agentData, categoryData, trendData] = await Promise.all([
        supportTicketService?.getSupportAnalytics(),
        supportTicketService?.getAgentPerformance(),
        supportTicketService?.getIssueCategoryBreakdown(),
        supportTicketService?.getTicketTrends()
      ]);

      setAnalytics(analyticsData);
      setAgentPerformance(agentData);
      setCategoryBreakdown(categoryData);
      setTicketTrends(trendData);
    } catch (err) {
      setError(err?.message || 'Failed to load support analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleExport = () => {
    // Export analytics data
    const exportData = {
      analytics,
      agentPerformance,
      categoryBreakdown,
      ticketTrends,
      exportedAt: new Date()?.toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `support-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Icon name="BarChart3" className="w-8 h-8 text-blue-600" />
                Support Analytics & Performance Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Comprehensive insights into help desk operations, agent performance, and client satisfaction metrics
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator isRefreshing={refreshing} lastRefresh={new Date()} />
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <Icon name="RefreshCw" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="Download" className="w-4 h-4" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mt-4 flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Time Period:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_90_days">Last 90 Days</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-900">Error Loading Analytics</h3>
              <p className="text-sm text-amber-800 mt-1">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader2" className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
              <p className="mt-4 text-gray-600">Loading analytics data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Executive Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <PerformanceSummaryCard
                title="Total Tickets"
                value={analytics?.totalTickets || 0}
                icon="Ticket"
                color="blue"
                subtitle={`${analytics?.openTickets || 0} open, ${analytics?.inProgressTickets || 0} in progress`}
              />
              <PerformanceSummaryCard
                title="Avg Resolution Time"
                value={`${analytics?.avgResolutionTimeHours || 0}h`}
                icon="Clock"
                color="green"
                subtitle="Average time to resolve"
              />
              <PerformanceSummaryCard
                title="Client Satisfaction"
                value={`${analytics?.avgSatisfactionRating || 0}/5`}
                icon="Star"
                color="yellow"
                subtitle="Average satisfaction rating"
              />
              <PerformanceSummaryCard
                title="First Contact Resolution"
                value={`${analytics?.firstContactResolutionRate || 0}%`}
                icon="CheckCircle"
                color="purple"
                subtitle="Resolved on first contact"
              />
            </div>

            {/* Main Analytics Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Ticket Trends */}
              <div className="lg:col-span-2">
                <TicketTrendChart data={ticketTrends} />
              </div>

              {/* Issue Category Breakdown */}
              <div>
                <IssueCategoryChart data={categoryBreakdown} />
              </div>
            </div>

            {/* Agent Performance & Satisfaction */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <AgentPerformanceTable data={agentPerformance} />
              <SatisfactionAnalysisPanel analytics={analytics} />
            </div>

            {/* SLA Compliance */}
            <div className="mb-8">
              <SLACompliancePanel analytics={analytics} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportAnalyticsPerformanceDashboard;
